import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Box, Button, TextField } from '@material-ui/core';
import {
  SText,
  SSelectDropdown,
  SelectDetailsButton,
  SelectMenuHeader,
  SEmptyState,
  SInfiniteScroll,
  SingleSelectLoader,
  SButton
} from '@setvi/shared/components';
import AddRoundedIcon from '@material-ui/icons/AddRounded';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import eventEmitter from '@setvi/shared/libs/EventEmitter';
import { SearchIcon } from '@setvi/shared/components/sicons';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getCompetitorsQuery } from 'Services';
import { CompetitorInterface } from 'Services/Query/Competitors/Types';
import { v4 as uuidv4 } from 'uuid';

export interface ModifiedCompetitorInterface extends CompetitorInterface {
  new?: boolean;
}

interface CompetitorDropdownProps {
  selected: ModifiedCompetitorInterface | null;
  onSelect: (competitor: ModifiedCompetitorInterface | null) => void;
  style?: { input?: CSSProperties; dropdownWrapperWidth?: number };
  width?: number;
}

const CompetitorDropdown = ({
  selected,
  onSelect,
  width = 550,
  style
}: CompetitorDropdownProps) => {
  const [anchor, setAnchor] = useState(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');

  const params = useMemo(
    () => ({
      page: 1,
      limit: 10,
      search: debouncedSearchQuery || undefined,
      sort: 'name'
    }),
    [debouncedSearchQuery]
  );

  const {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = useInfiniteQuery(getCompetitorsQuery(params));

  const fetchMore = useCallback(() => {
    if (hasNextPage) fetchNextPage();
  }, [hasNextPage, fetchNextPage]);

  const competitors = useMemo(() => {
    const list = data?.pages?.map(page => page?.data).flat(1) || [];

    return list;
  }, [data]);

  const handleSelect = (competitor: CompetitorInterface) => {
    onSelect(competitor);
    setAnchor(null);
  };

  const isSelected = (competitor: CompetitorInterface) =>
    selected?._id === competitor._id;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 200);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    eventEmitter.on('sdropdown-menu-opened', () => setAnchor(null));

    return () => {
      eventEmitter.off('sdropdown-menu-opened', () => setAnchor(null));
    };
  }, []);

  return (
    <SSelectDropdown
      withPopover
      newDesign
      anchor={
        <SelectDetailsButton
          width={width}
          noBorder
          style={style?.input}
          title={selected ? selected.name : `Select for Competitors`}
          titleHighligted={!!selected}
          fontColor="#5D7083"
          fontWeight="normal"
          fontSize="16px"
          onClick={e => setAnchor(e?.currentTarget)}
          open={Boolean(anchor)}
        />
      }
      label="Competitor"
      anchorElement={anchor}
      onClose={() => setAnchor(null)}
      content={
        <Box width={style?.dropdownWrapperWidth || width - 20}>
          <SelectMenuHeader
            title="Select Competitor"
            handleClear={() => {
              onSelect(null);
              setAnchor(null);
            }}
            clearButton
            searchValue={searchQuery}
            handleChange={setSearchQuery}
            placeholder="Search Competitor..."
            customSearch={
              <>
                <Box pt={2} mb={2}>
                  <TextField
                    style={{ width: '100%', color: '#5D7083' }}
                    placeholder="Type and create new"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <SearchIcon
                          style={{
                            transform: 'scale(0.7)'
                          }}
                        />
                      )
                    }}
                  />
                </Box>
                {competitors?.length === 0 &&
                  debouncedSearchQuery &&
                  !isLoading && (
                    <SButton
                      fullWidth
                      variant="text"
                      color="default"
                      size="small"
                      style={{
                        marginTop: 4,
                        fontWeight: 'normal',
                        color: '#333D47',
                        fontSize: 14,
                        justifyContent: 'flex-start',
                        borderRadius: 36
                      }}
                      startIcon={
                        <AddRoundedIcon
                          style={{
                            width: 25,
                            height: 25
                          }}
                          htmlColor="#5D7083"
                        />
                      }
                      onClick={() => {
                        onSelect({
                          _id: uuidv4(),
                          name: debouncedSearchQuery,
                          url: '',
                          createdAt: new Date(),
                          updatedAt: new Date(),
                          new: true
                        });
                        setAnchor(null);
                      }}>
                      Create new competitor "{debouncedSearchQuery}"
                    </SButton>
                  )}
              </>
            }
          />
          <Box height={300} overflow="auto">
            <Box>
              <SInfiniteScroll
                list={competitors}
                keyGetter={customer => customer._id}
                fetchMore={fetchMore}
                render={customer => (
                  <Button
                    fullWidth
                    style={{
                      display: 'flex',
                      justifyContent: 'start',
                      background: isSelected(customer)
                        ? '#EBF5FF'
                        : 'trasnparent'
                    }}
                    onClick={() =>
                      handleSelect(
                        selected?._id === customer?._id ? null : customer
                      )
                    }
                    endIcon={
                      <CheckRoundedIcon
                        htmlColor={
                          isSelected(customer) ? '#2196F3' : 'transparent'
                        }
                      />
                    }
                    variant="text"
                    color="default">
                    <Box display="flex" flexDirection="column" width="100%">
                      <SText
                        style={{ textAlign: 'left' }}
                        title={customer.name}
                        fontColor={isSelected(customer) ? '#2196F3' : undefined}
                      />
                    </Box>
                  </Button>
                )}
              />
            </Box>
            {isLoading ||
              isFetching ||
              (isFetchingNextPage && <SingleSelectLoader />)}
            {!isLoading && !isFetchingNextPage && competitors?.length === 0 && (
              <SEmptyState subTitle="No competitors found." />
            )}
          </Box>
        </Box>
      }
    />
  );
};

export default CompetitorDropdown;
